/** Klien API Web Admin: access token di memori, refresh token di cookie httpOnly (tidak terbaca JS). */
const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3100/v1';

export type Realm = 'tenant' | 'platform';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public extra: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

const tokens: Record<Realm, string | null> = { tenant: null, platform: null };
/** Sesi dukungan (impersonasi): token berumur pendek tanpa refresh; saat 401 sesi langsung berakhir. */
let impersonating = false;
export const setImpersonation = (token: string | null) => {
  impersonating = !!token;
  tokens.tenant = token;
};
export const isImpersonating = () => impersonating;
const refreshing: Partial<Record<Realm, Promise<boolean>>> = {};
const listeners = new Set<(realm: Realm) => void>();

export const setToken = (realm: Realm, t: string | null) => {
  tokens[realm] = t;
};
export const getToken = (realm: Realm) => tokens[realm];
/** Dipanggil saat sesi berakhir (refresh gagal) agar UI kembali ke halaman login. */
export const onSessionExpired = (fn: (realm: Realm) => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

const refreshPath = (realm: Realm) =>
  realm === 'platform' ? '/admin/auth/refresh' : '/auth/refresh';

export function refreshSession(realm: Realm): Promise<boolean> {
  refreshing[realm] ??= (async () => {
    try {
      const res = await fetch(BASE + refreshPath(realm), {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      tokens[realm] = ((await res.json()) as { accessToken: string }).accessToken;
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => delete refreshing[realm], 0);
    }
  })();
  return refreshing[realm]!;
}

async function raw(
  realm: Realm,
  method: string,
  path: string,
  body?: unknown,
  retry = true,
): Promise<Response> {
  const res = await fetch(BASE + path, {
    method,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(tokens[realm] ? { authorization: `Bearer ${tokens[realm]}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401 && retry && !path.includes('/auth/')) {
    if (!(realm === 'tenant' && impersonating) && (await refreshSession(realm)))
      return raw(realm, method, path, body, false);
    tokens[realm] = null;
    listeners.forEach((l) => l(realm));
  }
  return res;
}

async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const { title, code, status, errors: _e, ...extra } = data as Record<string, unknown>;
    const fieldErrors = Array.isArray(data.errors)
      ? data.errors
          .map((e: { field: string; message: string }) => `${e.field}: ${e.message}`)
          .join('; ')
      : '';
    throw new ApiError(
      res.status,
      String(code ?? 'ERROR'),
      fieldErrors || String(title ?? res.statusText),
      { ...extra, status },
    );
  }
  return data as T;
}

export const makeApi = (realm: Realm) => ({
  get: <T>(p: string) => raw(realm, 'GET', p).then((r) => parse<T>(r)),
  post: <T>(p: string, b?: unknown) => raw(realm, 'POST', p, b ?? {}).then((r) => parse<T>(r)),
  put: <T>(p: string, b: unknown) => raw(realm, 'PUT', p, b).then((r) => parse<T>(r)),
  patch: <T>(p: string, b: unknown) => raw(realm, 'PATCH', p, b).then((r) => parse<T>(r)),
  del: <T>(p: string) => raw(realm, 'DELETE', p).then((r) => parse<T>(r)),
});

export const API_ORIGIN = BASE.replace(/\/v1\/?$/, '');
export const fileUrl = (path: string | null | undefined) => (path ? API_ORIGIN + path : null);

/** Unggah multipart (field "file"). Jangan set content-type manual agar boundary diisi browser. */
export async function upload<T>(realm: Realm, path: string, file: File, retry = true): Promise<T> {
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(BASE + path, {
    method: 'POST',
    credentials: 'include',
    headers: tokens[realm] ? { authorization: `Bearer ${tokens[realm]}` } : {},
    body,
  });
  if (res.status === 401 && retry && (await refreshSession(realm)))
    return upload<T>(realm, path, file, false);
  return parse<T>(res);
}

/** Unduh berkas dengan kredensial (mis. ekspor CSV) lalu picu penyimpanan di browser. */
export async function download(realm: Realm, path: string, fallbackName: string): Promise<void> {
  const res = await raw(realm, 'GET', path);
  if (!res.ok) await parse(res);
  const name =
    /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = makeApi('tenant');
export const platformApi = makeApi('platform');
